<script setup lang="ts">
import type { MaskInputOptions } from "maska";

import { vMaska } from "maska/vue";
import { computed, reactive, ref, watch } from "vue";

import Modal from "@/components/modal/modal.vue";
import StepsButtons from "@/components/StepsButtons/StepsButtons.vue";
import it4UpdateIMEI from "@/hooks/requests/it4UpdateIMEI/it4UpdateIMEI";
import useTranslate from "@/hooks/useTranslate/useTranslate";
import { useChooseDeviceStore } from "@/stores/chooseDevice/chooseDevice";
import { useHowYouRateDeviceStore } from "@/stores/howYouRateDevice/howYouRateDevice";
import Checkbox from "@/ui/checkbox/checkbox.vue";
import Field from "@/ui/field/field.vue";
import AwardIcon from "@/ui/icons/award.vue";
import Input from "@/ui/input/input.vue";
import { BugTrackerSection, useBugTracker } from "~/tiw/helpers/bugTracker";
import parseStringPrice from "~/tiw/helpers/parseStringPrice";
import { createContract } from "~/tiw/helpers/singingType";
import createApplication from "~/tiw/hooks/requests/createApplication/createApplication";
import it4SetDeviceStatus from "~/tiw/hooks/requests/it4SetDeviceStatus/it4SetDeviceStatus";
import {
    useAnalyticsEventAgreement,
    useAnalyticsEventTiModel,
} from "~/tiw/hooks/useAnalytics/useAnalytics";
import useStoreResetter from "~/tiw/hooks/useStoreResetter/useStoreResetter";
import useValidate from "~/tiw/hooks/useValidate";
import { useEvaluationResultStore } from "~/tiw/stores/evaluationResult/evaluationResult";
import { useSuccessfulEvaluationStore } from "~/tiw/stores/successfulEvaluation/successfulEvaluation";
import { useTIWStore } from "~/tiw/stores/tiw/tiw";
import { DatePicker } from "~/tiw/ui/datepicker";

import HandedDevice from "../../handedDevice/handedDevice.vue";

const TIWStore = useTIWStore();
const SuccessfulEvaluationStore = useSuccessfulEvaluationStore();
const HowYouRateDeviceStore = useHowYouRateDeviceStore();
const ChooseDeviceStore = useChooseDeviceStore();
const EvaluationResultStore = useEvaluationResultStore();

const loadingToNext = ref<boolean>(false);

const isImeiAlreadyInputed = ref(false);
watch(
    () => ChooseDeviceStore.field_imei.value,
    (imei) => {
        if (imei) {
            isImeiAlreadyInputed.value = true;
        }
    },
    { immediate: true, once: true },
);

function onEditHandler() {
    useStoreResetter();
    TIWStore.switchStep("chooseDevice");
    useAnalyticsEventTiModel();
}

async function createContractB2C() {
    SuccessfulEvaluationStore.contractData = await createContract({
        token: TIWStore.WIDGET_DATA.token,
        deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
        lang: TIWStore.standardizedLocale,
        firstName: SuccessfulEvaluationStore.individualFields.name,
        lastName: SuccessfulEvaluationStore.individualFields.surname,
        middleName: "",
        mobile: SuccessfulEvaluationStore.individualFields.phone,
        email: SuccessfulEvaluationStore.individualFields.email,
        doc: SuccessfulEvaluationStore.individualFields.PESEL,
        docNum: SuccessfulEvaluationStore.individualFields.idNumber,
        docIssueDate: SuccessfulEvaluationStore.individualFields.dateOfIssue,
        docPostCode: SuccessfulEvaluationStore.individualFields.postalCode,
        docCityName: SuccessfulEvaluationStore.individualFields.city,
        docStreet: SuccessfulEvaluationStore.individualFields.street,
        docHouseNum: SuccessfulEvaluationStore.individualFields.houseNumber,
        docApartmentNum: SuccessfulEvaluationStore.individualFields.apartmentNumber,
        sign: TIWStore.IS_DISCOUNT_LATER ? false : !TIWStore.IS_DISCOUNT_AFTER_TRADING,
        hold: TIWStore.HOLD,
    });
}

const handedDevicePrice = computed(() => {
    if (typeof HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale === "object") {
        return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale?.tradeInSale;
    }

    return HowYouRateDeviceStore?.calculatedDeviceData?.panelTradeInSale;
});

const nextStepBtnText = computed(() => {
    if (TIWStore.IS_DISCOUNT_AFTER_TRADING) {
        return useTranslate("tiw__chooseSigningType__signingType__withoutSms__title");
    } else {
        return useTranslate("tiw__receivingContractViaSms");
    }
});

async function clickToNext() {
    loadingToNext.value = true;

    useAnalyticsEventAgreement();

    if (!isImeiAlreadyInputed.value) {
        await it4UpdateIMEI({
            article: TIWStore.INJECTED_GOOD.article,
            token: TIWStore.WIDGET_DATA.token,
            imei: ChooseDeviceStore.field_imei.value,
            deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
        });
    }

    try {
        await createContractB2C();
    } catch (e) {
        useBugTracker(
            "Successful Evaluation --- [POST] Create Contract B2C",
            BugTrackerSection.ERROR,
            {
                message: e?.message,
            },
        );
        loadingToNext.value = false;
        return;
    }

    if (SuccessfulEvaluationStore.isPeselAlreadyUsedForContract) {
        TIWStore.switchStep("successfulEvaluationFailed");
        return;
    }

    await it4SetDeviceStatus({
        token: TIWStore.WIDGET_DATA.token,
        deviceId: HowYouRateDeviceStore.registeringDeviceData.deviceId,
        status: TIWStore.IS_DISCOUNT_LATER ? "PRELIMINARY_ASSESSED" : "ONLINE_CONTRACT_GENERATED",
    });

    if (TIWStore.IS_DISCOUNT_AFTER_TRADING) {
        try {
            await createApplication({
                device_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
                ex_id: HowYouRateDeviceStore.registeringDeviceData.deviceId,
                imei: ChooseDeviceStore.field_imei.value,
                locale: TIWStore.standardizedLocale,
                location: TIWStore.WIDGET_DATA.location,
                phone: SuccessfulEvaluationStore.individualFields.phone.replace(/\D+/g, ""),
                source: TIWStore.WIDGET_DATA.code,
                city_name: SuccessfulEvaluationStore.individualFields.city,
                email: SuccessfulEvaluationStore.individualFields.email,
                first_name: SuccessfulEvaluationStore.individualFields.name,
                last_name: SuccessfulEvaluationStore.individualFields.surname,
                grade: HowYouRateDeviceStore.registeringDeviceData.grade,
                brand: ChooseDeviceStore.brandName,
                model: ChooseDeviceStore.field_model.value.name,
                price: parseStringPrice(handedDevicePrice.value),
                address: SuccessfulEvaluationStore.address,
                session: TIWStore.SESSION_TOKEN,
            });
        } catch (e) {
            useBugTracker(
                "Successful Evaluation --- [POST] Create Application",
                BugTrackerSection.ERROR,
                {
                    message: e?.message,
                },
            );
            loadingToNext.value = false;
            return;
        }
    }

    if (TIWStore.IS_DISCOUNT_LATER) {
        TIWStore.switchStep("signingWithoutSms");
    } else {
        TIWStore.switchStep("confirmSigned");
    }
    loadingToNext.value = false;
}

const PESELValidation = computed(() => {
    if (!SuccessfulEvaluationStore.individualFields.PESEL) {
        return {
            status: true,
            message: "ok",
        };
    }

    return {
        status: SuccessfulEvaluationStore.individualFields.PESEL.length === 11,
        message: useTranslate("tiw__errors__mustBeNumber").replace("[[NUMBER]]", "11"),
    };
});

const phoneMaskOptions = reactive<MaskInputOptions>({
    mask: "+48 ___ ___-___",
    tokens: {
        _: {
            pattern: /\d/,
        },
        A: {
            pattern: /[1-9]/,
        },
    },
    eager: true,
});

const phoneValidation = computed(() => {
    if (!SuccessfulEvaluationStore.individualFields.phone) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "phone",
        value: SuccessfulEvaluationStore.individualFields.phone,
    });
    return valiDate;
});

const emailValidation = computed(() => {
    if (!SuccessfulEvaluationStore.individualFields.email) {
        return {
            status: true,
            message: "ok",
        };
    }
    const valiDate = useValidate({
        target: "email",
        value: SuccessfulEvaluationStore.individualFields.email,
    });
    return valiDate;
});

const isAcceptedToNext = computed<boolean>(() => {
    const fields: {
        status: boolean;
        message: string;
    }[] = [];
    fields.push(emailValidation.value, phoneValidation.value, PESELValidation.value);

    const validationStatus = fields.every((el) => el.status);

    return validationStatus && SuccessfulEvaluationStore.isAcceptedToNext;
});
</script>

<template>
    <Modal>
        <template #header>
            <p class="tiw__step__successfulEvaluation__header tiw__h2">
                <AwardIcon />
                {{ useTranslate("tiw__successfulEvaluation__title") }}
            </p>
            <p>
                {{ useTranslate("tiw__successfulEvaluation__knowExactly") }}
            </p>
        </template>

        <div class="tiw__step__successfulEvaluation">
            <HandedDevice
                :device-price="handedDevicePrice"
                :device-chain="ChooseDeviceStore.deviceChain"
                :device-state="HowYouRateDeviceStore.rate"
                :device-preview="ChooseDeviceStore.deviceImagePreview"
                :device-grade="HowYouRateDeviceStore.deviceGrade"
                :device-no-grade="EvaluationResultStore.stateOfEvaluation === 'UNKNOWN'"
                :device-terminated="EvaluationResultStore.stateOfEvaluation === 'TERMINATED'"
                :on-edit="onEditHandler"
            />
            <form name="successfulEvaluationFormIndividual">
                <Field>
                    <template #title>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__imei_title") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="ChooseDeviceStore.field_imei.value"
                            type="text"
                            name="imei"
                            :disabled="isImeiAlreadyInputed"
                        />
                    </template>
                </Field>
                <Field :is-error="!PESELValidation.status">
                    <template #title>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__dataForContract") }}
                        </span>
                    </template>
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__PESEL") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.PESEL"
                            v-maska="'###########'"
                            :disabled="!TIWStore.HOLD"
                            maxlength="11"
                            type="text"
                            name="PESEL"
                        />
                    </template>
                    <template v-if="!PESELValidation.status" #hintBottom>
                        <span>
                            {{ PESELValidation.message }}
                        </span>
                    </template>
                </Field>
                <Field>
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__name") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.name"
                            :disabled="!TIWStore.HOLD"
                            type="text"
                            name="name"
                        />
                    </template>
                </Field>
                <Field>
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__surname") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.surname"
                            :disabled="!TIWStore.HOLD"
                            type="text"
                            name="surname"
                        />
                    </template>
                </Field>
                <Field>
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__idNumber") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.idNumber"
                            :disabled="!TIWStore.HOLD"
                            type="text"
                            name="idNumber"
                        />
                    </template>
                </Field>
                <Field>
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__dateOfIssue") }}
                        </span>
                    </template>
                    <template #body>
                        <DatePicker
                            v-model="SuccessfulEvaluationStore.individualFields.dateOfIssue"
                            :disabled="!TIWStore.HOLD"
                            name="dateOfIssue"
                        />
                    </template>
                </Field>
                <!-- <Field>
          <template #hintTop>
            <span>
              {{ "Expiration Date" }}
            </span>
          </template>
          <template #body>
            <DatePicker v-model="SuccessfulEvaluationStore.individualFields.expirationDate" name="expirationDate" />
          </template>
        </Field> -->
                <Field>
                    <template #title>
                        <span>
                            {{ useTranslate("tiw__successfulEvaluation__AddressOfResidence") }}
                        </span>
                    </template>
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__postalCode") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.postalCode"
                            type="text"
                            name="postalCode"
                        />
                    </template>
                </Field>
                <Field>
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__city") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.city"
                            type="text"
                            name="city"
                        />
                    </template>
                </Field>
                <Field>
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__street") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.street"
                            type="text"
                            name="street"
                        />
                    </template>
                </Field>
                <div class="tiw__step__successfulEvaluation__houseApartment">
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__houseNumber") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="SuccessfulEvaluationStore.individualFields.houseNumber"
                                type="text"
                                name="houseNumber"
                            />
                        </template>
                    </Field>
                    <Field>
                        <template #hintTop>
                            <span>
                                {{ useTranslate("tiw__forms__apartmentNumber") }}
                            </span>
                        </template>
                        <template #body>
                            <Input
                                v-model="SuccessfulEvaluationStore.individualFields.apartmentNumber"
                                type="text"
                                name="apartmentNumber"
                            />
                        </template>
                    </Field>
                </div>
                <Field :is-error="!emailValidation.status">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__email") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.email"
                            type="text"
                            name="email"
                        />
                        <p class="tiw__step__successfulEvaluation__emailDecs">
                            {{ useTranslate("tiw__weWillSendInstructions") }}
                        </p>
                    </template>
                    <template v-if="!emailValidation.status" #hintBottom>
                        <span>
                            {{ emailValidation.message }}
                        </span>
                    </template>
                </Field>
                <Field :is-error="!phoneValidation.status">
                    <template #hintTop>
                        <span>
                            {{ useTranslate("tiw__forms__phone") }}
                        </span>
                    </template>
                    <template #body>
                        <Input
                            v-model="SuccessfulEvaluationStore.individualFields.phone"
                            v-maska="phoneMaskOptions"
                            type="text"
                            name="phone"
                            :placeholder="phoneMaskOptions.mask"
                        />
                    </template>
                    <template v-if="!phoneValidation.status" #hintBottom>
                        <span>
                            {{ phoneValidation.message }}
                        </span>
                    </template>
                </Field>
                <Checkbox
                    v-model="SuccessfulEvaluationStore.individualFields.personal"
                    :checked="SuccessfulEvaluationStore.individualFields.personal"
                >
                    <span v-html="useTranslate('tiw__successfulEvaluation__checkbox_personal')" />
                </Checkbox>
                <Checkbox
                    v-if="TIWStore.IS_DISCOUNT_LATER"
                    v-model="SuccessfulEvaluationStore.individualFields.isFinalPriceOkay"
                    :checked="SuccessfulEvaluationStore.individualFields.isFinalPriceOkay"
                >
                    <span
                        v-html="
                            useTranslate('tiw__successfulEvaluation__checkbox_isFinalPriceOkay')
                        "
                    />
                </Checkbox>
                <Checkbox
                    v-model="SuccessfulEvaluationStore.individualFields.duty"
                    :checked="SuccessfulEvaluationStore.individualFields.duty"
                >
                    <span>
                        {{ useTranslate("tiw__successfulEvaluation__checkbox_duty_head") }}:
                    </span>
                    <ul class="tiw__step__successfulEvaluation__checkbox_duty_list">
                        <li>
                            {{ useTranslate("tiw__successfulEvaluation__checkbox_duty_step1") }}
                        </li>
                        <li>
                            {{ useTranslate("tiw__successfulEvaluation__checkbox_duty_step2") }}
                        </li>
                        <li>
                            {{ useTranslate("tiw__successfulEvaluation__checkbox_duty_step3") }}
                        </li>
                    </ul>
                    <span>
                        {{ useTranslate("tiw__successfulEvaluation__checkbox_duty_footer") }}
                    </span>
                </Checkbox>
            </form>

            <StepsButtons
                :btn-text="nextStepBtnText"
                :disabled="!isAcceptedToNext || loadingToNext"
                :click-to-next="clickToNext"
            />
        </div>
    </Modal>
</template>

<style lang="scss" src="./successfulEvaluation.scss"></style>
